import { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { deletePreference, setPreference, usePreference } from '@fs/zion-preferences'
import { useUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import { useEvent } from '@fs/zion-frontend-friends'
import type { RootstechPromoDateFlag } from '../types'
import {
  EXPANDED_COOKIE,
  DISMISS_COOKIE,
  DISMISS_PREFERENCE,
  DISMISS_SESSION_FLAG,
  COOKIE_EXPIRATION_DAYS,
  PROMO_DATE_FLAG,
} from '../constants'
import { getCrossTabChannel } from '../channel'

// Clears stale dismiss state when the promo reset date advances past the user's dismissal time.
function useDateReset(
  resetDate: number | null,
  hideFromCookie: string | undefined,
  dismissedPref: string | undefined
): boolean {
  const [applied, setApplied] = useState(false)

  useEffect(() => {
    if (!resetDate || Date.now() < resetDate) return

    // Always clear the collapsed cookie on a date reset so the promo reopens expanded,
    // even if the user only collapsed without dismissing.
    Cookies.remove(EXPANDED_COOKIE)

    if (hideFromCookie && Number(hideFromCookie) < resetDate) {
      Cookies.remove(DISMISS_COOKIE)
      setApplied(true)
    }

    if (dismissedPref && Number(dismissedPref) < resetDate) {
      deletePreference(DISMISS_PREFERENCE)?.catch(console.error)
    }
  }, [hideFromCookie, dismissedPref, resetDate])

  return applied
}

export function useHidePromo(): [boolean, () => void] {
  const { config } = useFeatureFlag<RootstechPromoDateFlag>(PROMO_DATE_FLAG)
  const resetDate = config?.date ? Date.parse(config.date) || null : null
  const { signedIn } = useUser()
  const hideFromCookie = Cookies.get(DISMISS_COOKIE)
  const dismissedPref = usePreference(DISMISS_PREFERENCE)
  const [hidden, setHidden] = useState(false)

  const dateResetApplied = useDateReset(resetDate, hideFromCookie, dismissedPref)

  const dismissedTime = dismissedPref ? Number(dismissedPref) : null
  // hideFromPref is true when a dismissal exists and hasn't been invalidated by a past reset.
  // If the reset date is in the future (no reset has fired yet), all dismissals are still valid.
  const hideFromPref = Boolean(dismissedTime && (!resetDate || Date.now() < resetDate || dismissedTime > resetDate))

  // Read session flag once at mount — set when a logged-out user dismisses the promo,
  // so we know to sync the cookie value into their preference on next login.
  const [pendingSync] = useState(() => sessionStorage.getItem(DISMISS_SESSION_FLAG) === '1')

  // When logged in with a pending sync, write the cookie timestamp into the preference then clear the flag.
  useEffect(() => {
    if (!signedIn || !pendingSync) return
    const cookieTime = hideFromCookie ? Number(hideFromCookie) : null
    if (cookieTime) {
      setPreference(DISMISS_PREFERENCE, cookieTime)?.catch(console.error)
    }
    sessionStorage.removeItem(DISMISS_SESSION_FLAG)
  }, [signedIn, pendingSync, hideFromCookie])

  // When logged in with no pending sync, keep cookie in sync with preference (restores expired cookies).
  useEffect(() => {
    if (!signedIn || pendingSync || !hideFromPref || hideFromCookie) return
    Cookies.set(DISMISS_COOKIE, String(dismissedTime), { expires: COOKIE_EXPIRATION_DAYS })
  }, [signedIn, pendingSync, hideFromPref, hideFromCookie, dismissedTime])

  const hide = useEvent(() => {
    setHidden(true)
    getCrossTabChannel()?.postMessage({ type: 'dismiss' })
    Cookies.set(DISMISS_COOKIE, String(Date.now()), { expires: COOKIE_EXPIRATION_DAYS })
    if (signedIn) {
      setPreference(DISMISS_PREFERENCE, Date.now())?.catch(console.error)
    } else {
      sessionStorage.setItem(DISMISS_SESSION_FLAG, '1')
    }
  })

  // Preference is king for signed-in users (once any pending sync has resolved).
  // While a pending sync is in progress or the user is logged out, the cookie determines visibility.
  const preferenceIsKing = Boolean(signedIn && !pendingSync)
  const isHidden = preferenceIsKing
    ? Boolean(hidden || hideFromPref)
    : Boolean(hidden || (hideFromCookie && !dateResetApplied))

  return [isHidden, hide]
}
